import React from 'react';
import Icon from '../../../components/AppIcon';

const OnboardingProgress = ({ currentStep, totalSteps }) => {
  const steps = [
    {
      number: 1,
      title: 'Agent Information',
      description: 'Personal & business details'
    },
    {
      number: 2,
      title: 'Business Verification',
      description: 'Documents & compliance'
    },
    {
      number: 3,
      title: 'Territory Selection',
      description: 'Choose service areas'
    },
    {
      number: 4,
      title: 'Commission Structure',
      description: 'Review earning potential'
    },
    {
      number: 5,
      title: 'Final Review',
      description: 'Submit application'
    }
  ];

  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepIcon = (stepNumber, status) => {
    if (status === 'completed') {
      return <Icon name="Check" size={16} className="text-white" />;
    }
    return <span className="text-sm font-medium">{stepNumber}</span>;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Registration Progress</h3>
        <span className="text-sm text-muted-foreground">
          Step {currentStep} of {totalSteps}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Overall Progress</span>
          <span className="text-sm text-muted-foreground">
            {Math.round((currentStep / totalSteps) * 100)}%
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Steps List */}
      <div className="space-y-4">
        {steps?.map((step, index) => {
          const status = getStepStatus(step?.number);
          
          return (
            <div 
              key={step?.number} 
              className="flex items-start space-x-4"
            >
              {/* Step Icon */}
              <div className={`
                flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-200
                ${status === 'completed' 
                  ? 'bg-success border-success' 
                  : status === 'current' ?'bg-primary border-primary text-white' :'bg-background border-muted-foreground/30 text-muted-foreground'
                }
              `}>
                {getStepIcon(step?.number, status)}
              </div>

              {/* Step Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <h4 className={`
                    text-sm font-medium transition-colors duration-200
                    ${status === 'completed' || status === 'current'
                      ? 'text-foreground' :'text-muted-foreground'
                    }
                  `}>
                    {step?.title}
                  </h4>
                  {status === 'current' && (
                    <span className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-full">
                      Current
                    </span>
                  )}
                  {status === 'completed' && (
                    <Icon name="CheckCircle" size={16} className="text-success" />
                  )}
                </div>
                <p className={`
                  text-xs mt-1 transition-colors duration-200
                  ${status === 'completed' || status === 'current'
                    ? 'text-muted-foreground' :'text-muted-foreground/60'
                  }
                `}>
                  {step?.description}
                </p>
              </div>

              {/* Connection Line */}
              {index < steps?.length - 1 && (
                <div className="absolute left-[15px] mt-8 w-0.5 h-6 bg-border" />
              )}
            </div>
          );
        })}
      </div>

      {/* Estimated Time */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Clock" size={16} />
            <span>Estimated time remaining:</span>
          </div>
          <span className="font-medium text-foreground">
            {Math.max(0, (totalSteps - currentStep) * 3)} minutes
          </span>
        </div>
      </div>
    </div>
  );
};

export default OnboardingProgress;